import React, { Component } from 'react';
import { action, computed, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import autoBindMethods from 'class-autobind-decorator';
import cx from 'classnames';
import { find, get, noop, pick, round, sortBy } from 'lodash';

import {
  Button,
  ButtonToolbar,
  Col,
  Grid,
  Modal,
  Row,
} from 'react-bootstrap';

import ClientsClass from '../../../clients/ClientsClass';
import { AppConstants } from '../../../constants';
import { Form } from '../../common-formsy';
import { IContractTerm, IContractTermDisplay, ILoan, ILoanAccount, IPresetContractTemplate } from '../../../interfaces';
import { Icon, Loader } from '../../common';
import { FormFieldSets, FormManager, IFieldSetPartial } from '../../../lib/mighty-fields';

import LoanTermsTable from './LoanTermsTable';
import { describeRateBand, isRateOutOfBand, rateBandOf, termsAsPercentages } from './terms';

const { CENT, CONTRACT_TEMPLATE_TYPE_LOAN, INTEREST_RATE_FRACTION_DECIMAL_PLACES } = AppConstants;

// Form on the left, contract terms on the right, as on the contract modal
const COL_WIDTHS = { form: 5, stacked: 12, terms: 7 };

// What an edit cannot reach back and change, said plainly rather than worked out per loan: both are
// snapshots the API takes on purpose, so a corrected figure here does not re-open what has been
// charged. See LoanSerializer.update.
const EDIT_WARNING = 'Balances are worked out again from what you save here. Compliance charges already '
  + 'levied keep the amounts they were priced on, and the account\'s compliance payment schedule does '
  + 'not move.';

// The terms are sent as the preset defined them, less the fields the API works out for itself.
const TERM_FIELDS = [
  'duration',
  'interest_accrual_duration',
  'interest_rate',
  'interest_type',
  'order',
  'principal_ref',
  'schedule_bucket_duration',
];

function fieldSets (templates: IPresetContractTemplate[]): IFieldSetPartial[] {
  return [
    [{
      field: 'contract_template',
      objectKey: 'id',
      objects: templates,
      required: true,
      type: 'objectSelect',
    }],
    [
      { field: 'initial_principal_amount', label: 'Initial Principal', required: true, type: 'money' },
      { field: 'origination_date', required: true, type: 'date' },
    ],
  ];
}

interface IProps {
  // Whether the footer offers Delete. The row option opens this modal for a user holding either
  // permission, so an editor without delete gets the form and nothing else.
  canDelete?: boolean;
  // The loan being edited, or nothing when the modal is adding one. Its own fields are read again
  // below: the table's row carries neither the template nor the terms.
  loan?: ILoan;
  loanAccount: ILoanAccount;
  onClose: () => void;
  // Deleting is the table's to run, the list around it being what has to be re-read afterwards, so
  // the footer button asks for it rather than doing it here.
  onRequestDelete?: () => void;
  onSave: () => Promise<void>;
}

interface IInjected extends IProps {
  Clients: ClientsClass;
}

@inject('Clients')
@autoBindMethods
@observer
class AddLoanModal extends Component<IProps> {
  @observable private isLoading = true;
  @observable private templates: IPresetContractTemplate[] = [];
  @observable private template: null | IPresetContractTemplate = null;
  @observable private terms: IContractTerm[] = [];
  // The loan as the API reports it, which is where an edit's starting values come from. Null while it
  // loads, and on the add path, which starts from an empty form.
  @observable private loan: null | ILoan = null;
  // Built from the values the form starts on, so on the edit path it cannot exist until the loan has
  // been read -- the body shows a loader until it does. c.f. EditableCard, which builds one per edit.
  @observable private formManager: null | FormManager = null;
  // Whether the load failed. Without it a failure reads as a load that never finishes, the form being
  // wired to a manager that was never built.
  @observable private hasFailed = false;

  public componentWillMount () {
    this.fetchFormData();
  }

  private get isEditing () {
    return !!this.props.loan;
  }

  private get injected () {
    return this.props as IInjected;
  }

  // Not @action: a mobx 3 action does not extend across an await, so the assignments below it would
  // run outside the action anyway.
  private async fetchFormData () {
    const { Clients } = this.injected
      , { loan } = this.props
      ;

    this.isLoading = true;
    this.hasFailed = false;

    try {
      // Filtered server-side: a lien template here would be refused on save, having terms the
      // loan engine cannot price.
      const [templates, editing]: [IPresetContractTemplate[], null | ILoan] = await Promise.all([
        Clients.presetContractTemplates.list({ params: { contract_type: CONTRACT_TEMPLATE_TYPE_LOAN } }),
        loan ? Clients.loans.retrieve(loan.id) : Promise.resolve(null),
      ]);

      this.templates = this.templateOptions(templates, editing);
      this.loan = editing;

      if (editing) {
        // Set before the manager is built, so the first change event the form reports -- which carries
        // the template it started on -- reads as no change and leaves the terms below alone.
        this.template = find(this.templates, { id: editing.contract_template }) || null;
        // The loan's own terms rather than its template's: a template edited since the loan was
        // written must not rewrite what the loan was drawn up under.
        this.terms = (editing.terms || []).map(term => ({ ...term }));
      }

      this.buildFormManager(editing);
    }
    catch (error) {
      // Transport has already toasted the failure; swallowing it here avoids a second toast. The body
      // says so too rather than sitting on a loader: without the loan there is no form to offer, and
      // without the templates there is no template to draw one up against.
      this.hasFailed = true;
      // tslint:disable-next-line no-console
      console.error('Error fetching the loan form data:', error);
    }
    finally {
      this.isLoading = false;
    }
  }

  /**
   * The templates the dropdown offers: the published ones, plus the template the loan being edited was
   * drawn up against if that has since been unpublished.
   *
   * Its own template has to stay on the list. Left off, the field renders empty on a loan that has one,
   * and the first change to any other field resolves the id to nothing and takes the loan's terms down
   * with it -- which the save would then write.
   */
  private templateOptions (templates: IPresetContractTemplate[], loan: null | ILoan) {
    const published = templates.filter(template => template.is_published)
      , ownTemplate = loan ? find(templates, { id: loan.contract_template }) : null
      , offered = ownTemplate && !find(published, { id: ownTemplate.id })
        ? [...published, ownTemplate]
        : published
      ;

    return sortBy(offered, 'order');
  }

  // The field configs are handed over again at render, the manager only needing the field names, which
  // do not change.
  private buildFormManager (loan: null | ILoan) {
    this.formManager = new FormManager({
      fieldSets: fieldSets([]),
      model: loan ? pick(loan, ['contract_template', 'initial_principal_amount', 'origination_date']) : {},
      onChange: this.onFormChange,
      onSave: this.onSave,
      onSuccess: this.props.onClose,
    });
  }

  // Picking a template is what fills in the terms, so the rest of the form starts from its defaults.
  @action
  private onFormChange (data: { contract_template?: string }) {
    // Both sides are normalised to null: the form reports an unset dropdown as '', so comparing it
    // to the unset template's undefined would miss and reset the terms on every other keystroke.
    const selectedId = data.contract_template || null;

    if (get(this.template, 'id', null) === selectedId) { return; }

    const template = selectedId ? find(this.templates, { id: selectedId }) || null : null;

    // A template the list does not carry is not a template the user picked -- templateOptions keeps the
    // edited loan's own on it -- so nothing is read from it and the terms stay as they are.
    if (selectedId && !template) { return; }

    const terms: IContractTerm[] = template ? template.terms : [];

    this.template = template;
    this.terms = terms.map(term => ({ ...term }));
  }

  @computed
  private get fieldSets () {
    return fieldSets(this.templates);
  }

  /**
   * The period the terms table heads itself with. An edited loan keeps the one it was drawn up under for
   * as long as its own template is the one selected -- the template's may have been changed since -- and
   * picking a different template brings that template's along with its terms.
   *
   * Null included: a loan drawn up under no minimum period is held to none, whatever its template says
   * today, and the loan's own balances are worked out that way.
   */
  @computed
  private get minimumDuration (): null | string {
    if (this.loan && this.template && this.template.id === this.loan.contract_template) {
      return this.loan.minimum_duration || null;
    }

    return get(this.template, 'minimum_duration', null);
  }

  @computed
  private get termsAsPercentages (): IContractTermDisplay[] {
    return termsAsPercentages(this.terms);
  }

  @computed
  private get isLocked (): boolean {
    return !!get(this.template, 'is_locked');
  }

  @computed
  private get rateBand () {
    return rateBandOf(this.template);
  }

  /**
   * What to say while a rate sits outside the band the template allows, and null while none does.
   *
   * Only while the template is unlocked, that being the only time a rate can be retyped. A locked one
   * would otherwise leave an edit of a loan written before the band was set stuck, with no field to
   * correct.
   */
  @computed
  private get rateBandError (): null | string {
    if (this.isLocked) { return null; }

    const isOutOfBand = this.termsAsPercentages.some(term => isRateOutOfBand(term.interest_rate, this.rateBand));

    return isOutOfBand ? describeRateBand(this.rateBand) : null;
  }

  /**
   * The form's own props, less its save while a rate sits outside the band. The disabled button is not
   * the only way to submit -- Enter in a text field is another -- and the footer says why either way.
   */
  @computed
  private get formProps () {
    if (!this.formManager) { return {}; }

    return this.rateBandError
      ? { ...this.formManager.formProps, onValidSubmit: noop }
      : this.formManager.formProps;
  }

  private setTermPercentage (idx: number, value: string) {
    // Rounding to the percentage's own precision would drop two of the decimal places the user is
    // allowed to type, turning 3.2567% into 3.26%.
    this.terms[idx].interest_rate = String(round(Number(value) / CENT, INTEREST_RATE_FRACTION_DECIMAL_PLACES));
  }

  private async onSave (data: object) {
    const { Clients } = this.injected
      , { loan, loanAccount, onSave } = this.props
      // Terms go up on an edit as they do on a create: the API replaces the loan's with these, which is
      // how picking a different template moves the loan onto its terms.
      , payload = { ...data, terms: this.terms.map(term => pick(term, TERM_FIELDS)) }
      ;

    if (loan) {
      await Clients.loans.update(loan.id, payload);
    }
    else {
      await Clients.loans.create({ ...payload, account: loanAccount.id });
    }

    await onSave();
  }

  private renderBody () {
    if (this.isLoading) {
      return <Loader className='modal-loader' />;
    }

    // The manager is what the fields are wired to, so without one there is no form to show. c.f.
    // LoanDetailsModal, which tells a failed read apart from a loan the engine cannot price.
    if (this.hasFailed || !this.formManager) {
      return (
        // .page-error is how the loan account page itself says this, and it is the same message
        <div className='page-error'>
          {this.isEditing
            ? 'An error occurred loading this loan. Close this and try again.'
            : 'An error occurred loading the contract templates. Close this and try again.'}
        </div>
      );
    }

    return (
      <Grid className='col-wrapper' fluid>
        {/* One row only: .col-wrapper is a flex row, so a second one would take half the modal */}
        <Row>
          <Col className='col-left' lg={COL_WIDTHS.form} md={COL_WIDTHS.form} sm={COL_WIDTHS.stacked} xs={COL_WIDTHS.stacked}>
            {this.isEditing && (
              <p className='loan-edit-warning'><Icon type='exclamation-triangle' /> {EDIT_WARNING}</p>
            )}
            <FormFieldSets fieldSets={this.fieldSets} {...this.formManager.formGroupsProps} />
          </Col>
          <Col className='col-right' lg={COL_WIDTHS.terms} md={COL_WIDTHS.terms} sm={COL_WIDTHS.stacked} xs={COL_WIDTHS.stacked}>
            <LoanTermsTable
              isLocked={this.isLocked}
              minimumDuration={this.minimumDuration}
              onChange={this.setTermPercentage}
              rateBand={this.rateBand}
              terms={this.termsAsPercentages}
            />
          </Col>
        </Row>
      </Grid>
    );
  }

  // Deleting is destructive and the account's other tabs read the loan, so it says what it does and
  // leaves the confirming to the table.
  private renderDeleteButton () {
    if (!this.props.canDelete) { return null; }

    return (
      <Button bsStyle='danger' className='btn-delete-loan pull-left' onClick={this.props.onRequestDelete}>
        <Icon type='trash' /> Delete loan
      </Button>
    );
  }

  private renderFooter () {
    const { onClose } = this.props
      // Absent only while the loan loads, which is also when there is nothing to submit
      , formManager = this.formManager
      ;

    return (
      <Modal.Footer>
        {formManager && formManager.errorMessages.map(error => (
          <Row className='text-danger' key={error.field}>
            <strong>{error.field}</strong> - {error.message}
          </Row>
        ))}

        {this.rateBandError && (
          <Row className='text-danger'>
            <strong>terms</strong> - {this.rateBandError}
          </Row>
        )}

        {this.renderDeleteButton()}

        <ButtonToolbar className='pull-right'>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            bsStyle='primary'
            disabled={this.isLoading || !!this.rateBandError || !formManager || formManager.submitDisabled()}
            type='submit'
          >
            {this.submitText}
          </Button>
        </ButtonToolbar>
      </Modal.Footer>
    );
  }

  @computed
  private get submitText () {
    if (this.formManager && this.formManager.isSubmitting) { return 'Saving...'; }

    return this.isEditing ? 'Save loan' : 'Create new loan';
  }

  public render () {
    const { onClose } = this.props
      , modalClassNames = cx(
        this.isEditing ? 'modal-edit-loan' : 'modal-new-loan',
        'right side-modal large two-col',
      )
      ;

    return (
      <Modal show onHide={onClose} className={modalClassNames}>
        {/* formProps carry the submit handler, so the form waits for the manager the same as the body */}
        <Form {...this.formProps}>
          <Modal.Header closeButton>
            <Modal.Title>{this.isEditing ? 'Edit Loan' : 'Add New Loan'}</Modal.Title>
          </Modal.Header>

          <Modal.Body>{this.renderBody()}</Modal.Body>

          {this.renderFooter()}
        </Form>
      </Modal>
    );
  }
}

export default AddLoanModal;
